// Subscriptions → Add-ons (F-BOOT-17, spec/14 §4.5): the Provider's catalogue of add-ons, and the price of each. Clavian Boot
// is the first, priced per PC a month. The catalogue starts empty; until the Provider sets a price, Clavian Boot cannot
// be attached to any organisation.
//
// Money is integer minor units plus a currency, always. The Provider types a price in the currency's major unit and
// `parsePrice` turns it into minor units exactly — split at the decimal point, never multiplied, no float. A new price
// applies to add-ons attached afterwards; one already attached keeps the price it was attached at. `hq:subscriptions`
// gates every route here; anyone else is told it is not theirs.

import type { Addon } from '@clavian/api-client';
import { addonMonthlyCharge } from '@clavian/domain/addons';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Badge, Button, Dialog, EmptyState, Switch, Table, TextField, useToast, type TableColumn } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { problemDetail } from '../../api/problems';
import { minorToMajor } from '../../org/money';
import { Icon } from '../../shell/nav-icons';
import { LoadFailure } from '../../shell/LoadFailure';
import { Notice, Section, TableSkeleton } from '../../ui';
import { RequirePlatformPermission } from '../access';
import { HqHeading } from '../heading';
import { useAddonCatalogue, useCreateCatalogueAddon, useUpdateCatalogueAddon } from '../organizations/addons-queries';
import { HQ_SUBSCRIPTIONS } from '../sections';
import { parsePrice, type PriceParse } from './price';

/** Clavian Boot's key, name and the currency a price is first offered in (the platform launches in India). */
const BOOT_KEY = 'boot';
const BOOT_NAME = 'Clavian Boot';
const FIRST_CURRENCY = 'INR';
/** How many PCs the example under the price field is worked for. */
const EXAMPLE_PCS = 10;

export function AddonCataloguePage(): ReactNode {
  return (
    <RequirePlatformPermission permission={HQ_SUBSCRIPTIONS}>
      <AddonCatalogue />
    </RequirePlatformPermission>
  );
}

function AddonCatalogue() {
  const { t } = useTranslation('hq');
  const catalogue = useAddonCatalogue();
  const [editing, setEditing] = useState<Addon | 'new' | undefined>(undefined);
  const boot = catalogue.data?.data.find((addon) => addon.key === BOOT_KEY);

  let body: ReactNode;
  if (catalogue.data === undefined) {
    body = catalogue.isError ? (
      <LoadFailure error={catalogue.error} message={t('subscriptions.unavailable')} onRetry={() => void catalogue.refetch()} />
    ) : (
      <TableSkeleton rows={2} label={t('subscriptions.loading')} />
    );
  } else if (catalogue.data.data.length === 0) {
    body = (
      <EmptyState
        icon={<Icon name="subscriptions" />}
        title={t('subscriptions.empty.title')}
        description={t('subscriptions.empty.body', { name: BOOT_NAME })}
        action={
          <Button variant="primary" onClick={() => setEditing('new')}>
            {t('subscriptions.empty.action', { name: BOOT_NAME })}
          </Button>
        }
      />
    );
  } else {
    body = <CatalogueTable addons={catalogue.data.data} onEdit={setEditing} />;
  }

  return (
    <div className="console-page">
      <header className="console-releases-head">
        <HqHeading title={t('subscriptions.title')} tip={t('subscriptions.intro')} />
      </header>
      <Section title={t('subscriptions.addons.title')} description={t('subscriptions.addons.intro')} body="rows">
        {body}
      </Section>
      {editing === undefined ? null : <PriceDialog addon={editing === 'new' ? boot : editing} onClose={() => setEditing(undefined)} />}
    </div>
  );
}

/** The example's monthly charge, or `null` where the sum would pass the largest amount the api holds. */
function exampleCharge(price: Addon['price']): Addon['price'] | null {
  try {
    return addonMonthlyCharge(price, EXAMPLE_PCS);
  } catch {
    return null;
  }
}

function CatalogueTable({ addons, onEdit }: { readonly addons: readonly Addon[]; readonly onEdit: (addon: Addon) => void }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const columns: readonly TableColumn<Addon>[] = [
    { key: 'name', header: t('subscriptions.columns.name'), cell: (addon) => <strong>{addon.name}</strong> },
    { key: 'pricing', header: t('subscriptions.columns.pricing'), cell: (addon) => t(`subscriptions.pricing.${addon.pricing}`) },
    { key: 'price', header: t('subscriptions.columns.price'), numeric: true, cell: (addon) => t('subscriptions.perPcMonth', { price: format.money(addon.price) }) },
    {
      key: 'offered',
      header: t('subscriptions.columns.offered'),
      cell: (addon) => (
        <Badge tone={addon.active ? 'ok' : 'neutral'} dot>
          {addon.active ? t('subscriptions.offered') : t('subscriptions.notOffered')}
        </Badge>
      ),
    },
    { key: 'updated', header: t('subscriptions.columns.updated'), cell: (addon) => format.date(addon.updatedAt, 'medium') },
    {
      key: 'actions',
      header: <span className="clav-visually-hidden">{t('subscriptions.columns.actions')}</span>,
      cell: (addon) => (
        <Button variant="secondary" size="sm" onClick={() => onEdit(addon)}>
          {t('subscriptions.edit')}
        </Button>
      ),
    },
  ];
  return <Table caption={t('subscriptions.addons.title')} captionHidden columns={columns} rows={addons} rowKey={(addon) => addon.key} />;
}

const REASON_KEY: Readonly<Record<Exclude<PriceParse, { ok: true }>['reason'], string>> = {
  currency: 'subscriptions.dialog.errors.currency',
  empty: 'subscriptions.dialog.errors.empty',
  invalid: 'subscriptions.dialog.errors.invalid',
  fraction: 'subscriptions.dialog.errors.fraction',
  whole: 'subscriptions.dialog.errors.whole',
  tooLarge: 'subscriptions.dialog.errors.tooLarge',
};

/** Offer Clavian Boot with its first price, or change a catalogue entry's price, name and whether it is offered. */
function PriceDialog({ addon, onClose }: { readonly addon: Addon | undefined; readonly onClose: () => void }) {
  const { t } = useTranslation('hq');
  const format = useVenueFormatter();
  const { show } = useToast();
  const create = useCreateCatalogueAddon();
  const update = useUpdateCatalogueAddon();
  const [price, setPrice] = useState(addon === undefined ? '' : minorToMajor(addon.price.amount, addon.price.currency));
  const [currency, setCurrency] = useState(addon?.price.currency ?? FIRST_CURRENCY);
  const [offered, setOffered] = useState(addon?.active ?? true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);
  const name = addon?.name ?? BOOT_NAME;
  const parsed = parsePrice(price, currency);
  const pending = create.isPending || update.isPending;

  function submit(event: FormEvent): void {
    event.preventDefault();
    setFailure(undefined);
    if (!parsed.ok) {
      setError(t(REASON_KEY[parsed.reason]));
      return;
    }
    setError(undefined);
    const done = () => {
      onClose();
      show({ tone: 'ok', title: t('subscriptions.dialog.saved', { name }) });
    };
    const failed = (caught: unknown) => setFailure(problemDetail(caught) ?? t('subscriptions.dialog.failed'));
    if (addon === undefined) {
      create.mutate({ key: BOOT_KEY, name: BOOT_NAME, pricing: 'per_pc', price: parsed.money, active: offered }, { onSuccess: done, onError: failed });
    } else {
      update.mutate({ addonKey: addon.key, body: { price: parsed.money, active: offered } }, { onSuccess: done, onError: failed });
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => (next ? undefined : onClose())}
      size="sm"
      title={addon === undefined ? t('subscriptions.dialog.offerTitle', { name }) : t('subscriptions.dialog.editTitle', { name })}
      description={t('subscriptions.dialog.intro')}
    >
      <form className="console-form" noValidate aria-label={t('subscriptions.dialog.formLabel', { name })} onSubmit={submit}>
        {failure === undefined ? null : (
          <Notice tone="danger" role="alert">
            {failure}
          </Notice>
        )}
        <TextField
          label={t('subscriptions.dialog.price', { currency: parsed.ok ? parsed.money.currency : currency.toUpperCase() })}
          description={t('subscriptions.dialog.priceHint')}
          required
          inputMode="decimal"
          autoComplete="off"
          value={price}
          error={error}
          onChange={(event) => setPrice(event.target.value)}
        />
        {addon === undefined ? (
          <TextField label={t('subscriptions.dialog.currency')} description={t('subscriptions.dialog.currencyHint')} required autoComplete="off" maxLength={3} value={currency} onChange={(event) => setCurrency(event.target.value)} />
        ) : (
          <p className="console-muted">{t('subscriptions.dialog.currencyFixed', { currency: addon.price.currency })}</p>
        )}
        {parsed.ok && exampleCharge(parsed.money) !== null ? (
          <p className="console-muted clav-figure" role="status">
            {t('subscriptions.dialog.example', { pcs: EXAMPLE_PCS, total: format.money(exampleCharge(parsed.money) as Addon['price']), price: format.money(parsed.money) })}
          </p>
        ) : null}
        <Switch label={t('subscriptions.dialog.offered')} checked={offered} onCheckedChange={setOffered} />
        <p className="console-muted">{t('subscriptions.dialog.snapshotNote')}</p>
        <div className="console-form__actions">
          <Button variant="ghost" onClick={onClose}>
            {t('subscriptions.dialog.cancel')}
          </Button>
          <Button type="submit" variant="primary" busy={pending}>
            {addon === undefined ? t('subscriptions.dialog.offer') : t('subscriptions.dialog.save')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
